import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {Checkbox, colors, FormControl, FormControlLabel, Input, InputLabel, ListItemText, MenuItem, Radio, RadioGroup, Select, Typography, withStyles} from '@material-ui/core'
import { FilePond } from 'react-filepond'
import classNames from 'classnames'
import 'filepond/dist/filepond.css'
import ImageDropper from '../../components/FormElement/ImageDropper/ImageDropper'
import FormElementType from './FormElementType'

const styles = {
    inputForm: {
        paddingBottom: '2em'
    },

    textForm: {
        display: 'flex',
        flex: 1
    },

    imageDropperForm: {
        marginTop: '1em'
    },

    imageDropperError: {
        border: `2px solid ${colors.red[400]}`,
        paddingBottom: 0,
        marginBottom: '2em'
    },

    inputFormRadio: {
        paddingTop: '1.3em',
        paddingBottom: '1.3em',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center'
    },

    inputFormRadioGroup: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center'
    },

    radioButtonRoot: {
        color: colors.green[600],
        '&$radioButtonChecked': {
            color: colors.green[500]
        }
    },
    radioButtonChecked: {},

    inputFormRadioLabel: {

    },

    errorMessage: {
        color: colors.red[500],
        position: 'absolute',
        bottom: '0em',
        alignSelf: 'baseline',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        left: 0,
        right: '1em',
        textOverflow: 'ellipsis'
    },

    errorMessageRegular: {
        color: colors.red[500],
        paddingBottom: '0.5em'
    },

    filePond: {
        borderRadius: '0.5em',
        backgroundColor: '#f1f0ef',
        color: '#4f4f4f'
    },

    filePondError: {
        border: `2px solid ${colors.red[400]}`,
        marginBottom: '1em'
    },

    errorLabel: {
        '&$labelFocused': {
            color: colors.red[500]
        }
    },

    errorUnderline: {
        '&:before': {
            borderBottomColor: colors.red[500]
        },
        '&:after': {
            borderBottomColor: colors.red[500]
        }
    },

    label: {
        '&$labelFocused': {
            color: colors.green[500]
        }
    },
    labelFocused: {},
    underline: {
        '&:before': {
            borderBottomColor: colors.green[500]
        },
        '&:after': {
            borderBottomColor: colors.green[500]
        }
    },
    checkBox: {
        color: colors.green[500],
        '&$checked': {
            color: colors.green[500]
        }
    },
    checked: {}
}

const getDefaultValue = function (elementType, props = {}) {
    switch (elementType) {
    case FormElementType.Select: return []
    case FormElementType.MultiFile: return []
    case FormElementType.File: return []
    case FormElementType.CheckBox: return false
    case FormElementType.Radio: return Object.keys(props.options).reverse()[0]
    default: return ''
    }
}

const requiredRule = /.+/

class FormElement extends Component {
    getDefaultState = () => ({
        value: getDefaultValue(this.props.elementType, this.props),
        touched: false,
        isValid: (!this.props.required || this.props.disabled || this.props.defaultImg),
        errorMessage: false,
        innerDataIsValid: false
    })

    state = this.getDefaultState()

    componentDidUpdate(prevProps, prevState) {
        if ((this.state.isValid !== prevState.isValid) && this.props.onValidationChangeDetected) this.props.onValidationChangeDetected()
        if (prevProps.disabled !== this.props.disabled) this.updateValidation(this.state.value, false)
        if (prevProps.defaultImg !== this.props.defaultImg) this.updateValidation(this.state.value, false)
    }

    clear = () => {
        if (this.imageDropper) this.imageDropper.clear()
        if (this.pond) this.pond.removeFiles()
        this.setState(this.getDefaultState())
    }

    setValue = value => {
        this.updateValidation(value, false)
    }

    getErrorMessage = (validations, value) => {
        const {label, required, minLength, maxLength, elementType, disabled, defaultImg} = this.props

        if (disabled) return false

        switch (elementType) {
        case FormElementType.TextField:
            if (required && !value.match(requiredRule)) return `${label} is required.`
            if (required && value.trim().length < minLength) return `${label} must be between (${minLength} - ${maxLength}) characters.`
            if (!required && value.trim().length > 0 && value.trim().length < minLength) return `${label} must be between (${minLength} - ${maxLength}) characters.`
            break
        case FormElementType.Select:
            if (required && value.length === 0) return `${label} is required.`
            break

        case FormElementType.MultiFile:
            if (required && value.length === 0) return 'Select a file to upload'
            break
        case FormElementType.ImageDropper:
            if (defaultImg) break
            if (required && value.length === 0) return 'Select images to upload'
            break
        default: break
        }

        for (let index = 0; index < validations.length; index += 1) if (!value.match(validations[index].regex)) return validations[index].message
        return false
    }

    updateValidation = (value, dirty) => {
        const errorMessage = this.getErrorMessage(this.props.validations, value)
        if (this.props.onChange) this.props.onChange(value)
        this.setState({touched: this.state.touched || (dirty && !this.state.touched), errorMessage, value, isValid: errorMessage === false})
    }

    onBlur = () => this.updateValidation(this.state.value, true)

    onFilePondInit = () => this.updateValidation(this.pond.getFiles().map(file => file.file), false)

    onFilePondUpdated = files => this.updateValidation(files.map(file => file.file), true)

    onImageDropperInit = ref => {
        this.imageDropper = ref || this.imageDropper
        if (ref) this.updateValidation(ref.state.files, false)
    }

    onImageDropperUpdated = files => this.updateValidation(files, true)

    onImageDropperValidationChanged = isValid => {
        this.setState({innerDataIsValid: isValid}, this.props.onValidationChangeDetected)
    }

    onChange = event => {
        const {target: {value}} = event
        if (this.props.elementType === FormElementType.CheckBox) {
            this.setState({value: value === 'false'})
            if (this.props.onChange) this.props.onChange(value === 'false')
        } else {
            this.updateValidation(value, false)
        }
    }

    renderFormComponent = () => {
        const { classes: styles, label, type, name, minLength, maxLength, elementType, defaultOption, options, required, multiple, multiline, rows, rowsMax, filePondProps, hasCaption, disabled, imagePreviewSize, defaultImg, save, defaultCaption1, defaultCaption2, onDelete, withDropZone } = this.props
        const error = !this.props.disabled && this.state.touched && this.state.errorMessage && true

        switch (elementType) {
        case FormElementType.TextField:
            return (
                <FormControl required={required} disabled={disabled} className={classNames(styles.inputForm, styles.textForm)}>
                    <InputLabel
                        htmlFor={`${name}-input`}
                        FormLabelClasses={{
                            root: error ? styles.errorLabel : styles.label,
                            focused: styles.labelFocused
                        }}
                    >
                        {label}
                    </InputLabel>
                    <Input
                        id={`${name}-input`}
                        inputProps={{minLength, maxLength, name, type}}
                        onBlur={this.onBlur}
                        multiline={multiline}
                        rows={rows}
                        rowsMax={rowsMax}
                        onChange={this.onChange}
                        value={this.state.value}
                        classes={{
                            underline: error ? styles.errorUnderline : styles.underline
                        }}
                    />
                    {error && <Typography className={styles.errorMessage}>{this.state.errorMessage}</Typography>}
                </FormControl>
            )

        case FormElementType.Select:
            return (
                <FormControl required={required} className={styles.inputForm}>
                    <InputLabel
                        htmlFor={`${name}-select`}
                        FormLabelClasses={{
                            root: error ? styles.errorLabel : styles.label,
                            focused: styles.labelFocused
                        }}
                    >{label}
                    </InputLabel>
                    <Select
                        value={this.state.value}
                        onChange={this.onChange}
                        onBlur={this.onBlur}
                        className={styles.underline}
                        multiple={multiple}
                        inputProps={{
                            name,
                            id: `${name}-select`
                        }}
                        renderValue={selected => options && (multiple ? selected.map(item => options[item]).join(',') : selected)}
                    >
                        {Object.keys(defaultOption).map((key, index) => (
                            <MenuItem key={index} value={defaultOption} disabled>
                                <Checkbox disabled />
                                <ListItemText primary={key} />
                            </MenuItem>
                        ))}
                        {options && Object.keys(options).map((key, index) => (
                            <MenuItem key={index} value={key}>
                                <Checkbox classes={{
                                    root: styles.checkBox,
                                    checked: styles.checked
                                }} checked={this.state.value.indexOf(key) > -1}
                                />
                                <ListItemText primary={options[key]} />
                            </MenuItem>
                        ))}
                    </Select>
                    {error && <Typography className={styles.errorMessage}>{this.state.errorMessage}</Typography>}
                </FormControl>
            )

        case FormElementType.Radio:
            return (
                <FormControl className={styles.inputFormRadio} required={required}>
                    <Typography variant="h5" color="inherit" className={styles.title}>
                        {label}
                    </Typography>
                    <RadioGroup
                        aria-label={label}
                        name={name}
                        className={styles.inputFormRadioGroup}
                        value={this.state.value}
                        onChange={this.onChange}
                    >
                        {Object.keys(options).reverse()
                            .map((key, index) => (
                                <FormControlLabel key={index} value={key} control={
                                    <Radio classes={{
                                        root: styles.radioButtonRoot,
                                        checked: styles.radioButtonChecked
                                    }}
                                    />} label={options[key]}
                                />
                            ))}
                    </RadioGroup>
                </FormControl>
            )

        case FormElementType.MultiFile:
            return (
                <div>
                    <FilePond
                        className={classNames(styles.filePond, error && styles.filePondError)}
                        style={{backgroundColor: 'red'}}
                        required={required}
                        ref={ref => this.pond = ref}
                        labelIdle={label}
                        oninit={this.onFilePondInit}
                        onupdatefiles={this.onFilePondUpdated}
                        {...filePondProps}
                    />
                </div>
            )

        case FormElementType.ImageDropper:
            return (
                <div className={classNames(styles.inputForm, styles.imageDropperForm, error && styles.imageDropperError)}>
                    <ImageDropper disabled={disabled}
                        onValidationChangeDetected={this.onImageDropperValidationChanged}
                        innerRef={this.onImageDropperInit}
                        onChange={this.onImageDropperUpdated}
                        name={name}
                        label={label}
                        multiple={multiple}
                        hasCaption={hasCaption}
                        previewSize={imagePreviewSize}
                        defaultImg={defaultImg}
                        save={save}
                        defaultCaption1={defaultCaption1}
                        defaultCaption2={defaultCaption2}
                        onDelete={onDelete}
                        withDropZone={withDropZone}
                    />
                </div>
            )

        case FormElementType.CheckBox:
            return (
                <FormControlLabel
                    control={
                        <Checkbox
                            name={name}
                            checked={this.state.value}
                            value={this.state.value.toString()}
                            onChange={this.onChange}
                            classes={{
                                root: styles.checkBox,
                                checked: styles.checked
                            }}
                        />
                    }
                    label={label}
                />
            )

        default: return (
            <div>
                <h2>Error</h2>
            </div>
        )
        }
    }

    render() {
        return this.renderFormComponent()
    }
}

FormElement.propTypes = {
    classes: PropTypes.object.isRequired,
    value: PropTypes.string,
    elementType: PropTypes.oneOf(Object.keys(FormElementType)),
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    onValidationChangeDetected: PropTypes.func,
    type: PropTypes.string,
    options: PropTypes.object,
    multiple: PropTypes.bool,
    multiline: PropTypes.bool,
    rowsMax: PropTypes.number,
    defaultOption: PropTypes.object,
    required: PropTypes.bool,
    name: PropTypes.string,
    minLength: PropTypes.number,
    maxLength: PropTypes.number,
    validations: PropTypes.arrayOf(PropTypes.shape({
        regex: PropTypes.any,
        message: PropTypes.string
    })),
    disabled: PropTypes.bool,
    imagePreviewSize: PropTypes.string,
    defaultImg: PropTypes.any,
    save: PropTypes.func
}

FormElement.defaultProps = {
    type: 'text',
    elementType: FormElementType.TextField,
    validations: [],
    multiple: false,
    multiline: false,
    rowsMax: 5,
    disabled: false
}

export default withStyles(styles)(FormElement);
